import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLeagueStore } from '../../lib/leagueStore';
import { supabase } from '../../lib/supabase';
import { acceptLeagueRules } from '../../lib/leagueRulesAcceptance';
import { isTslhTenant } from '../../lib/tenantStore';
import { EmbedBackButton } from '../shared/EmbedBackButton';
import { RichTextContent, isRichTextEmpty, sanitizeRichText } from '../shared/RichTextEditor';

const ARSENAL_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Anton&family=Open+Sans:wght@400;600;700&display=swap';

function useArsenalFonts() {
  useEffect(() => {
    const id = 'arsenal-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = ARSENAL_FONTS_URL;
    document.head.appendChild(link);
  }, []);
}

// Palette Arsenal (orange/noir) vs TSLH (blanc/noir, cf. index.css data-tenant="tslh")
const ARSENAL_PALETTE = {
  pageBg: '#ffffff',
  panelBg: '#1a1a1a',
  panelBorder: '#2a2a2a',
  headerBg: '#000001',
  accent: '#f5a623',
  eyebrow: '#f5a623',
  emptyText: '#7d7d7d',
  checkboxBg: '#111',
  checkboxBorder: '#2a2a2a',
  labelText: '#e1e1e1',
  buttonTextAccepted: '#000001',
  buttonBgDisabled: '#3a3f42',
  buttonTextDisabled: '#7d7d7d',
};

const TSLH_PALETTE = {
  pageBg: '#151519',
  panelBg: '#1c1c22',
  panelBorder: '#2a2a31',
  headerBg: '#0a0a0b',
  accent: '#ffffff',
  eyebrow: '#9aa0aa',
  emptyText: '#9aa0aa',
  checkboxBg: '#0a0a0b',
  checkboxBorder: '#2a2a31',
  labelText: '#ededf8',
  buttonTextAccepted: '#0a0a0b',
  buttonBgDisabled: '#2a2a31',
  buttonTextDisabled: '#9aa0aa',
};

export function LeagueRulesOnboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  useArsenalFonts();
  const palette = isTslhTenant() ? TSLH_PALETTE : ARSENAL_PALETTE;
  const openedFromBuildMyTeam = location.state?.from === '/build-my-team';

  const [rules, setRules] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!selectedLeague?.id) return;
    fetchRules(selectedLeague.id);
  }, [selectedLeague]);

  const fetchRules = async (leagueId: number) => {
    try {
      const { data, error } = await supabase
        .from('league_settings')
        .select('speceficruleexplication')
        .eq('league_id', leagueId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Erreur chargement règles:', error);
        return;
      }
      setRules(data?.speceficruleexplication ?? '');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!accepted || !selectedLeague?.id) return;
    acceptLeagueRules(selectedLeague.id);
    navigate('/build-my-team', { replace: true });
  };

  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center py-4 px-3"
      style={{ background: palette.pageBg, minHeight: '100%' }}
    >
      {openedFromBuildMyTeam && <EmbedBackButton fallbackPath="/build-my-team" />}
      <div
        className="league-rules-onboarding-panel"
        style={{
          maxWidth: 720,
          width: '100%',
          background: palette.panelBg,
          borderRadius: 12,
          border: `1px solid ${palette.panelBorder}`,
        }}
      >
        {/* En-tête avec branding Arsenal */}
        <div
          style={{
            background: palette.headerBg,
            borderBottom: `3px solid ${palette.accent}`,
            padding: '18px 28px 14px',
          }}
        >
          <p
            style={{
              fontFamily: 'Open Sans, sans-serif',
              color: palette.eyebrow,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 2,
              textTransform: 'uppercase',
              margin: '0 0 6px',
            }}
          >
            {selectedLeague?.name ?? 'POOL BPM'}
          </p>
          <h1
            style={{
              fontFamily: 'Anton, sans-serif',
              color: '#ffffff',
              fontSize: 'clamp(20px, 4vw, 30px)',
              letterSpacing: 1,
              margin: 0,
              lineHeight: 1.1,
            }}
          >
            RÈGLES DE LA LIGUE
          </h1>
        </div>

        {/* Corps */}
        <div style={{ padding: '18px 28px' }}>
          {loading ? (
            <div className="text-center py-5">
              <div
                className="spinner-border"
                style={{ color: palette.accent }}
                role="status"
              >
                <span className="visually-hidden">Chargement…</span>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 14 }}>
              {isRichTextEmpty(sanitizeRichText(rules)) ? (
                <div
                  style={{
                    fontFamily: 'Open Sans, sans-serif',
                    color: palette.emptyText,
                    fontStyle: 'italic',
                    fontSize: 14,
                    lineHeight: 1.5,
                  }}
                >
                  Aucune règle spécifique n'a encore été saisie par le commissaire.
                </div>
              ) : (
                <RichTextContent html={rules} variant="onboarding" />
              )}
            </div>
          )}

          {/* Case à cocher de confirmation */}
          <div
            style={{
              background: palette.checkboxBg,
              border: `1px solid ${palette.checkboxBorder}`,
              borderRadius: 8,
              padding: '12px 16px',
              marginBottom: 14,
            }}
          >
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                cursor: 'pointer',
                fontFamily: 'Open Sans, sans-serif',
                color: palette.labelText,
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                style={{
                  width: 20,
                  height: 20,
                  marginTop: 2,
                  accentColor: palette.accent,
                  flexShrink: 0,
                  cursor: 'pointer',
                }}
              />
              J'ai lu les règles et je suis prêt à construire mon équipe championne
            </label>
          </div>

          <button
            onClick={handleConfirm}
            disabled={!accepted}
            style={{
              width: '100%',
              background: accepted ? palette.accent : palette.buttonBgDisabled,
              color: accepted ? palette.buttonTextAccepted : palette.buttonTextDisabled,
              border: 'none',
              borderRadius: 8,
              padding: '12px 20px',
              fontFamily: 'Anton, sans-serif',
              fontSize: 16,
              letterSpacing: 1.5,
              cursor: accepted ? 'pointer' : 'not-allowed',
              transition: 'background 0.2s, color 0.2s',
            }}
          >
            CONSTRUIRE MON ÉQUIPE
          </button>
        </div>
      </div>
    </div>
  );
}
